import React, { Fragment, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
    BigModal,
    FlexDiv,
    ThemedButton,
} from '@awarego-ui/components';
import { useStores } from '../../../../hooks/use-stores';
import AutomationReminders from '../../../../components/manager/automation/reminders';
import ErrorMessage from '../../../../components/errormessage';
import { REMINDER_DELAY_TYPE, TRANSPORTS } from '../../../../utils/constants';

function cloneSteps(steps = []) {
    if (!steps.length) {
        return [
            {
                delay: 1,
                delayType: REMINDER_DELAY_TYPE.DAY,
            },
        ];
    }
    return steps.map((step) => ({
        delay: Number(step.delay) || 1,
        delayType: Number(step.delayType) || REMINDER_DELAY_TYPE.DAY,
        timeType: step.timeType != null ? Number(step.timeType) : 1,
        sendTime: step.sendTime,
    }));
}

function EditTrainingRemindersModal({ isOpen, onClose, companyId, training }) {
    const { automationStore, store, companyStore } = useStores();
    const { error, updatingRemindersSettings } = automationStore;

    const [enabled, setEnabled] = useState(false);
    const [channel, setChannel] = useState(TRANSPORTS.EMAIL);
    const [steps, setSteps] = useState([]);

    const slackConnected = Boolean(
        companyStore.slackDataIndex[companyId]?.connected
    );
    const teamsConnected = Boolean(
        companyStore.teamsDataIndex[companyId]?.connected &&
            companyStore.teamsDataIndex[companyId]?.enabled === '1'
    );

    useEffect(() => {
        if (!isOpen || !training) return;
        setEnabled(Boolean(training.reminders_enabled));
        setChannel(training.reminders_channel || TRANSPORTS.EMAIL);
        setSteps(cloneSteps(training.reminders_steps));
        automationStore.setError(null, 'saveRemindersSettings');
    }, [isOpen, training, automationStore]);

    const onChangeChannel = (event) => {
        const value = Number(event.target.value);
        const checked = event.target.checked;
        setChannel((current) => {
            let next = current;
            if (checked) next |= value;
            else next &= ~value;
            return next === 0 ? TRANSPORTS.EMAIL : next;
        });
    };

    const onAddReminderStep = () => {
        setSteps((current) => {
            if (current.length >= 5) return current;
            return [
                ...current,
                {
                    delay: Math.ceil(0.4 * Math.exp(current.length + 1)),
                    delayType: REMINDER_DELAY_TYPE.DAY,
                },
            ];
        });
    };

    const onDeleteReminderStep = (step) => {
        setSteps((current) => current.filter((s) => s !== step));
    };

    const onSave = async () => {
        const body = {
            enabled,
            channel,
        };
        if (enabled) {
            body.steps = steps.map((s) => ({
                delay: Number(s.delay),
                delayType: Number(s.delayType),
                timeType: s.timeType != null ? Number(s.timeType) : 1,
                sendTime: s.sendTime,
            }));
        }
        const result = await automationStore.saveRemindersSettings(
            companyId,
            training.id,
            body
        );
        if (result) onClose();
    };

    if (!training) return null;

    return (
        <BigModal
            isOpen={isOpen}
            onRequestClose={onClose}
            heading="Edit reminders"
            width={760}
            modalContent={
                <FlexDiv column gap={24}>
                    <ErrorMessage error={error} />
                    <AutomationReminders
                        reminders_enabled={enabled}
                        reminders_channel={channel}
                        reminders_steps={steps}
                        slackAvailable={store.slackAvailable}
                        teamsAvailable={store.teamsAvailable}
                        slackConnected={slackConnected}
                        teamsConnected={teamsConnected}
                        onChangeRemindersEnabled={() =>
                            setEnabled((value) => !value)
                        }
                        onChangeRemindersChannel={onChangeChannel}
                        onAddReminderStep={onAddReminderStep}
                        onDeleteReminderStep={onDeleteReminderStep}
                        onStepsChange={() => setSteps((current) => [...current])}
                        scheduleType={training.scheduleType}
                        mt={0}
                    />
                </FlexDiv>
            }
            modalFooterContent={
                <Fragment>
                    <ThemedButton
                        mode="primary"
                        text={updatingRemindersSettings ? 'Saving' : 'Save'}
                        $saving={Boolean(updatingRemindersSettings)}
                        disabled={
                            Boolean(updatingRemindersSettings) ||
                            (enabled && steps.length === 0)
                        }
                        onClick={onSave}
                    />
                    <ThemedButton
                        mode="secondary"
                        onClick={onClose}
                        text="Cancel"
                    />
                </Fragment>
            }
        />
    );
}

export default observer(EditTrainingRemindersModal);
